import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { CapsuleButton, ContentSpace, MainPageWithButtons } from "../_components";
import { useIsReadOnlyEmployee } from "../_helpers/useIsReadOnlyEmployee";
import { DeviceNotSoldWarning } from "../ModuleDeviceSearch/DeviceNotSoldWarning";
import { DEVICE_STATUS, formatDeviceDay, formatDeviceType } from "../ModuleDeviceSearch/deviceFormatters";
import { AssignLicenseView } from "../ModuleLicenses/AssignLicenseView";
import { LicenseActionButtons } from "../ModuleLicenses/LicenseActionButtons";
import { getLicenseStateConfig, hasLicenseActions } from "../ModuleLicenses/licenseStates";
import { getSellableLicenseTypes } from "../ModuleLicenses/getSellableLicenseTypes";
import { LocationDeviceDetail } from "./LocationDeviceDetail";

const PANE_ASSIGN = "ASSIGN";

export function LocationDetailView({ location, licenseTypes, onChange }) {

    const { t } = useTranslation();
    const isReadOnly = useIsReadOnlyEmployee();

    // Either PANE_ASSIGN or the bluetooth id of the device open
    const [openPane, setOpenPane] = useState(null);

    const stateConfig = getLicenseStateConfig(t);
    const canAssign = !isReadOnly && getSellableLicenseTypes(licenseTypes).length > 0;

    return (
        <ContentSpace
            mainContent={openPane ? null : (
                <MainPageWithButtons
                    pageTitle={`${location.locationName ?? "-"} (${location.locationId})`}
                    content={content()}
                    buttons={canAssign ? [
                        <CapsuleButton
                            key="assign"
                            onClick={() => setOpenPane(PANE_ASSIGN)}
                            text={t("mod_budgets_assign")}
                        />,
                    ] : []}
                    size={'X_LARGE'}
                />
            )}
            detailedContent={detailPane()}
            onGoBack={() => setOpenPane(null)}
        />
    );

    function detailPane() {
        if (!openPane)
            return null;

        if (openPane !== PANE_ASSIGN)
            return <LocationDeviceDetail bluetoothId={openPane} />;

        return (
            <AssignLicenseView
                licenseTypes={licenseTypes}
                initialLocationId={String(location.locationId)}
                initialEmail={location.adminEmail ?? ""}
                onAssignSuccess={() => {
                    setOpenPane(null);
                    onChange();
                }}
            />
        );
    }

    function content() {
        return (
            <div className="w-100">
                <div className="font-weight-bold text-uppercase">
                    {t("mod_device_search_admin_email")}
                </div>
                <div className="mb-4">{location.adminEmails.join(", ") || "-"}</div>

                <div className="font-weight-bold text-uppercase mb-2">
                    {t("mod_location_search_licenses")}
                </div>
                {licensesTable()}

                <div className="font-weight-bold text-uppercase mb-2 mt-4">
                    {t("mod_location_search_devices")}
                </div>
                {devicesTable()}
            </div>
        );
    }

    function licensesTable() {
        if (location.licenses.length === 0)
            return <div>{t("mod_location_search_no_licenses")}</div>;

        return (
            <div className="table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t("mod_budgets_licenses_table_type")}</th>
                            <th>{t("mod_budgets_licenses_table_valid_until")}</th>
                            <th>{t("mod_budgets_licenses_table_state")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {location.licenses.map((license, index) => licenseRow(license, index))}
                    </tbody>
                </table>
            </div>
        );
    }

    function licenseRow(license, index) {
        // Of other distributors only the type comes through
        if (!license.own) {
            return (
                <tr key={`other_${index}`}>
                    <td>{license.licenseTypeName}</td>
                    <td colSpan={3} className="text-muted">
                        {t("mod_location_search_other_distributor_license")}
                    </td>
                </tr>
            );
        }

        const licenseType = licenseTypes.find(type => type.uid === license.licenseTypeUID);
        const config = stateConfig[license.status];

        return (
            <tr key={license.uid}>
                <td>{license.licenseTypeName}</td>
                <td>{license.validUntil ? new Date(license.validUntil).toLocaleDateString() : "-"}</td>
                <td>
                    {config && <span className={`badge rounded-pill ${config.class}`}>{config.label}</span>}
                </td>
                <td className="text-right">
                    {!isReadOnly && hasLicenseActions(license, licenseType) &&
                        <LicenseActionButtons license={license} licenseType={licenseType} onDone={onChange} />
                    }
                </td>
            </tr>
        );
    }

    function devicesTable() {
        if (location.devices.length === 0)
            return <div>{t("mod_location_search_no_devices")}</div>;

        return (
            <div className="table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t("mod_device_search_bluetooth_id")}</th>
                            <th>{t("global_description")}</th>
                            <th>{t("global_type")}</th>
                            <th>{t("global_room")}</th>
                            <th>{t("mod_device_search_warranty_start")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {location.devices.map(device => (
                            <tr
                                key={device.uid}
                                style={{ cursor: "pointer" }}
                                onClick={() => setOpenPane(device.bluetoothId)}
                            >
                                <td className="text-monospace">
                                    {device.status === DEVICE_STATUS.NOT_SOLD && <DeviceNotSoldWarning iconOnly />}
                                    {" "}<span>{device.bluetoothId}</span>
                                </td>
                                <td>{device.description || "-"}</td>
                                <td>{formatDeviceType(device.type)}</td>
                                <td>{device.roomName || "-"}</td>
                                <td>{formatDeviceDay(device.warrantyStart)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
}
